// Course staff invites: an instructor invites someone with no Kudu instructor
// account to join a course as an additional instructor or an assistant. The
// backend returns proto messages as JSON; every field is read in snake_case
// and camelCase, and enums as numbers or names.

export type StaffRole = 'secondary_instructor' | 'assistant';

// CourseStaffRole in backend/proto/course.proto.
export const STAFF_ROLE_CODES: Record<StaffRole, number> = {
  secondary_instructor: 1,
  assistant: 2,
};

export type StaffInviteState =
  | 'pending'
  | 'expired'
  | 'accepted'
  | 'course_gone'
  | 'invalid';

export interface StaffInvite {
  createdAt: number | null;
  createdByName: string;
  email: string;
  expiresAt: number | null;
  firstName: string;
  key: string;
  lastName: string;
  role: StaffRole;
}

// What the public invite page shows for a key.
export interface StaffInviteView {
  courseTitle: string;
  email: string;
  firstName: string;
  inviterName: string;
  lastName: string;
  role: StaffRole | null;
  state: StaffInviteState;
}

export interface RawCourseStaffInvite {
  course_id?: string | null;
  courseId?: string | null;
  created_at_utc_milli?: number | string | null;
  createdAtUtcMilli?: number | string | null;
  created_by_name?: string | null;
  createdByName?: string | null;
  email?: string | null;
  expires_at_utc_milli?: number | string | null;
  expiresAtUtcMilli?: number | string | null;
  first_name?: string | null;
  firstName?: string | null;
  key?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  role?: number | string | null;
}

export interface RawCreateCourseStaffInviteResponse {
  already_pending?: boolean | null;
  alreadyPending?: boolean | null;
  invite?: RawCourseStaffInvite | null;
}

export interface RawGetCourseStaffInvitesResponse {
  invites?: RawCourseStaffInvite[] | null;
}

export interface RawRenewCourseStaffInviteResponse {
  invite?: RawCourseStaffInvite | null;
}

export interface RawGetCourseStaffInviteResponse {
  course_title?: string | null;
  courseTitle?: string | null;
  email?: string | null;
  first_name?: string | null;
  firstName?: string | null;
  inviter_name?: string | null;
  inviterName?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  role?: number | string | null;
  state?: number | string | null;
}

export interface RawAcceptCourseStaffInviteResponse {
  course_id?: string | null;
  courseId?: string | null;
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readMillis(value: unknown): number | null {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;

  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeStaffRole(value: unknown): StaffRole | null {
  if (value === 1 || value === '1' || value === 'COURSE_STAFF_SECONDARY_INSTRUCTOR') {
    return 'secondary_instructor';
  }

  if (value === 2 || value === '2' || value === 'COURSE_STAFF_ASSISTANT') {
    return 'assistant';
  }

  return null;
}

const INVITE_STATES: Record<string, StaffInviteState> = {
  '1': 'pending',
  '2': 'expired',
  '3': 'accepted',
  '4': 'course_gone',
  '5': 'invalid',
  ACCEPTED: 'accepted',
  COURSE_GONE: 'course_gone',
  EXPIRED: 'expired',
  INVALID: 'invalid',
  PENDING: 'pending',
};

export function normalizeStaffInviteState(value: unknown): StaffInviteState {
  return (
    (typeof value === 'number' || typeof value === 'string'
      ? INVITE_STATES[String(value)]
      : undefined) ?? 'invalid'
  );
}

export function normalizeStaffInvite(
  raw: RawCourseStaffInvite | null | undefined,
): StaffInvite | null {
  const key = readString(raw?.key);
  const role = normalizeStaffRole(raw?.role);

  if (!raw || !key || !role) {
    return null;
  }

  return {
    createdAt: readMillis(raw.created_at_utc_milli ?? raw.createdAtUtcMilli),
    createdByName: readString(raw.created_by_name ?? raw.createdByName),
    email: readString(raw.email),
    expiresAt: readMillis(raw.expires_at_utc_milli ?? raw.expiresAtUtcMilli),
    firstName: readString(raw.first_name ?? raw.firstName),
    key,
    lastName: readString(raw.last_name ?? raw.lastName),
    role,
  };
}

export function normalizeStaffInviteView(
  raw: RawGetCourseStaffInviteResponse | null | undefined,
): StaffInviteView {
  return {
    courseTitle: readString(raw?.course_title ?? raw?.courseTitle),
    email: readString(raw?.email),
    firstName: readString(raw?.first_name ?? raw?.firstName),
    inviterName: readString(raw?.inviter_name ?? raw?.inviterName),
    lastName: readString(raw?.last_name ?? raw?.lastName),
    role: normalizeStaffRole(raw?.role),
    state: normalizeStaffInviteState(raw?.state),
  };
}

// An invite counts as expired once its expiry has passed, even before the
// list is refetched.
export function isStaffInviteExpired(invite: StaffInvite, now: number): boolean {
  return invite.expiresAt !== null && now >= invite.expiresAt;
}

export function staffInviteFullName(invite: Pick<StaffInvite, 'firstName' | 'lastName'>): string {
  return `${invite.firstName} ${invite.lastName}`.trim();
}

export function staffRoleLabel(role: StaffRole): string {
  return role === 'secondary_instructor' ? 'Additional instructor' : 'Assistant';
}

// The sentence the invite form and the invite page use to say what the role
// allows.
export function staffRoleDescription(role: StaffRole): string {
  return role === 'secondary_instructor'
    ? 'an additional instructor with full edit rights'
    : 'an assistant with read-only access to answers and stats';
}
